/**
 * WBS Actuals — direct OData V4 read client against SAP S/4HANA (ACDOCA).
 *
 * Backed by the ZUI_M12_WBS_ACTUALS_O4 service binding (package ZMACH12),
 * exposing ZC_M12_WBS_ACTUALS: posted actual cost + quantity from the universal
 * journal, aggregated by company code, project, WBS element, fiscal period, and
 * cost element. Read-only; every request round-trips through the existing
 * `/api/s4/*` Vercel proxy -> ppm-s4-proxy -> BTP destination chain. There is no
 * local Supabase mirror here: this is the live SAP feed. The forecast-page sync
 * action persists the result into the `forecast_actuals` table.
 *
 * Browser request path:
 *   /api/s4/odata/v4/sap/opu/odata4/sap/zui_m12_wbs_actuals_o4/srvd/sap/z_m12_wbs_actuals_sd/0001/WbsActuals
 *
 * The `/sap/opu/odata4/` prefix is load-bearing (SCC whitelist), and the working
 * runtime path is `srvd` even though SAP publishes `srvd_a2x`
 * (see currencyRatesService.ts for the same two constraints).
 */

const SERVICE_PATH =
  '/api/s4/odata/v4/sap/opu/odata4/sap/zui_m12_wbs_actuals_o4/srvd/sap/z_m12_wbs_actuals_sd/0001/WbsActuals'

/** One aggregated ACDOCA actuals row as returned by the WbsActuals entity set.
 *  NOTE: the ABAP OData runtime serializes the NUMC fields FiscalPeriod and
 *  CostElementCategory WITHOUT leading zeros, e.g. "1", "3", "43". Treat them
 *  numerically (parseInt) rather than via string compares. */
export interface SapWbsActualRow {
  CompanyCode: string
  ProjectID: string
  WbsElement: string
  FiscalYear: string
  /** SAP posting period as returned: "1".."12" (no leading zero). */
  FiscalPeriod: string
  CostElement: string
  /** CO cost-element category (KATYP): "1" primary, "41"/"42" overhead,
   *  "43" activity allocation. Revenue/settlement/RA are excluded at source. */
  CostElementCategory: string
  /** Amount in company-code currency; OData returns Edm.Decimal as a number. */
  ActualCost: number
  /** Quantity in base unit (labor hours for time confirmations). */
  ActualQuantity: number
  Currency: string
  Unit: string
}

interface ODataCollectionResponse<T> {
  '@odata.context'?: string
  '@odata.nextLink'?: string
  value: T[]
}

/** Rows fetched per request. ZC_M12_WBS_ACTUALS aggregates by company code x
 *  project x WBS x period x cost element, so a 400-WBS project year runs to
 *  ~14,400 rows. One page is never the whole answer. */
const DEFAULT_PAGE_SIZE = 1000

/** Ceiling on REQUESTS, not on rows - the point is that the row count is not
 *  capped. It exists only so a service that answers every call with a full
 *  page plus a nextLink cannot spin forever; tripping it throws, because a
 *  partial read reported as complete is exactly the defect being fixed. */
const MAX_PAGES = 200

interface ODataErrorBody {
  error?: string | { code?: string; message?: string | { value?: string } }
  message?: string
  // ppm-s4-proxy CF envelope: { error: "s4_error", status, detail: <real SAP body> }
  status?: number
  detail?: ODataErrorBody | string
}

export class WbsActualsApiError extends Error {
  status: number
  code: string | null
  constructor(status: number, code: string | null, message: string) {
    super(message)
    this.status = status
    this.code = code
    this.name = 'WbsActualsApiError'
  }
}

function extractOData(
  body: ODataErrorBody | string | undefined,
  fallback: string,
): { code: string | null; message: string } {
  if (!body) return { code: null, message: fallback }
  if (typeof body === 'string') return { code: null, message: body || fallback }
  if (body.detail && (body.error === 's4_error' || typeof body.error === 'string')) {
    return extractOData(body.detail, fallback)
  }
  if (typeof body.error === 'object' && body.error) {
    const m = body.error.message
    const text = typeof m === 'string' ? m : m?.value
    return { code: body.error.code ?? null, message: text || fallback }
  }
  if (typeof body.message === 'string' && body.message) return { code: null, message: body.message }
  return { code: null, message: fallback }
}

async function unpackError(res: Response): Promise<WbsActualsApiError> {
  let body: ODataErrorBody | string | undefined
  try {
    body = (await res.json()) as ODataErrorBody
  } catch {
    try {
      body = await res.text()
    } catch {
      /* ignore */
    }
  }
  const fallback = `SAP returned HTTP ${res.status}`
  const { code, message } = extractOData(body, fallback)
  return new WbsActualsApiError(res.status, code, message)
}

/** App-format 'YYYY-MM' for a SAP row, zero-padding the no-leading-zero period. */
export function fiscalRowToPeriod(row: Pick<SapWbsActualRow, 'FiscalYear' | 'FiscalPeriod'>): string {
  const p = parseInt(row.FiscalPeriod, 10)
  return `${row.FiscalYear}-${String(Number.isFinite(p) ? p : 0).padStart(2, '0')}`
}

/** Re-base an `@odata.nextLink` onto the proxied service path.
 *
 *  SAP emits it relative to the service root ("WbsActuals?$skiptoken=..."),
 *  but some gateway configurations emit an absolute URL against the SAP host,
 *  which the /api/s4 proxy chain cannot serve. Only the query string is
 *  meaningful here (same entity set either way), so keep that and hang it off
 *  SERVICE_PATH. A link with no query carries nothing to ask for. */
function rebaseNextLink(nextLink: string): string | null {
  const q = nextLink.indexOf('?')
  return q < 0 ? null : `${SERVICE_PATH}${nextLink.slice(q)}`
}

/**
 * Reads all posted actuals for a project + fiscal year, optionally only through
 * a given period (inclusive snapshot cutoff). `throughPeriod` is an app-format
 * 'YYYY-MM'; periods after it are excluded so the sync snapshot lines up with
 * the EAC roll-forward freeze date.
 *
 * The period cutoff is applied client-side (not in the OData $filter) because
 * the runtime serializes FiscalPeriod without leading zeros, which makes a
 * string `le` comparison unreliable ("12" le "3"). We filter by project + year
 * in OData and trim by integer period here - per page, so a row past the
 * cutoff is never accumulated.
 *
 * Paging: this used to issue one `$top=5000` read and return `body.value` as
 * if it were the complete posted cost. A full 5,000-row page is
 * indistinguishable from a complete read, so the forecast preview summed a
 * third of a large project's cost, showed a confident total, and wrote that
 * snapshot; the same truncated set fed per-WBS AC, so CPI read high
 * project-wide. The read now runs to exhaustion: server-driven paging via
 * `@odata.nextLink` when the service offers it, `$skip` otherwise, stopping
 * only on a short page.
 *
 * `$orderby` on the semantic key is what makes `$skip` paging deterministic -
 * without a stable sort the same row can appear on two pages or on none.
 * (tcurrService.ts pages the same way for the same reason.)
 */
export async function listActualsForProject(
  projectId: string,
  fiscalYear: string,
  throughPeriod?: string,
  options: { pageSize?: number } = {},
): Promise<SapWbsActualRow[]> {
  const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE
  const esc = (v: string) => v.replace(/'/g, "''")
  const filter = `ProjectID eq '${esc(projectId)}' and FiscalYear eq '${esc(fiscalYear)}'`
  const orderby = 'CompanyCode asc, ProjectID asc, WbsElement asc, FiscalPeriod asc, CostElement asc'
  const pageUrl = (skip: number) =>
    `${SERVICE_PATH}?$filter=${encodeURIComponent(filter)}` +
    `&$orderby=${encodeURIComponent(orderby)}&$top=${pageSize}&$skip=${skip}`

  const cutoff = throughPeriod ? /^(\d{4})-(\d{2})$/.exec(throughPeriod) : null
  const maxPeriod =
    cutoff && cutoff[1] === fiscalYear ? parseInt(cutoff[2] ?? '0', 10) : null

  const rows: SapWbsActualRow[] = []
  let url = pageUrl(0)
  let skip = 0

  for (let page = 0; ; page++) {
    if (page >= MAX_PAGES) {
      throw new WbsActualsApiError(
        0,
        'PAGE_LIMIT',
        `Actuals read for ${projectId} / FY${fiscalYear} exceeded ${MAX_PAGES} pages. ` +
          'Refusing to report a partial read as complete.',
      )
    }

    const res = await fetch(url, { headers: { Accept: 'application/json' } })
    if (!res.ok) throw await unpackError(res)
    const body = (await res.json()) as ODataCollectionResponse<SapWbsActualRow>
    const batch = body.value ?? []

    for (const r of batch) {
      if (maxPeriod == null || parseInt(r.FiscalPeriod, 10) <= maxPeriod) rows.push(r)
    }

    const next = body['@odata.nextLink'] ? rebaseNextLink(body['@odata.nextLink']) : null
    if (next) {
      url = next
      continue
    }
    if (batch.length < pageSize) break
    skip += pageSize
    url = pageUrl(skip)
  }

  return rows
}
